import { zustandStorage } from "@helpers/storage";
import type { Apps } from "@memorang/constants/apps";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type Country = {
	type: string;
	title: string;
	image?: string;
	id?: string | undefined;
};

type Features = "hint" | "aila" | "haptic" | "notifications" | "alerts";

export type ConsentData = {
	privacy_notice?: {
		agreed: boolean;
		agreed_at: string;
	};
	mobile_app_terms?: {
		agreed: boolean;
		agreed_at: string;
	};
	ai_tool_terms?: {
		agreed: boolean;
		agreed_at: string;
	};
	marketing_emails?: {
		agreed: boolean;
	};
};

/* -----------------Types--------------- */
type State = {
	hasShownSummativeTestAlert?: boolean;
	hasShownConfidenceOnboardingDialog?: boolean;
	hasShownMasteryTopicsAlert?: boolean;
	lastLoggedInEmail?: string;
	showAvatarUpdatedMessage?: boolean;
	locale?: string;
	country?: Country;
	showAudioPlaybackTestDialog: boolean;
	isPremiumUser?: boolean;
	hasShownChallengeInstructions?: boolean;
	selectedApp?: Apps;
	pendingConsent?: ConsentData;
	adminPaywallEnabled?: boolean;
	isAuthFlowExamContextSwitching: boolean;
	autoRedeemLastPromptAt: Record<string, number>;
	autoRedeemPromptRequestId: number;
	shownQuestionTipTagIds: Record<string, boolean>;
	hideFutureQuestionTips?: boolean;
	setQuestionTipTagIdShown: (tagId: number) => void;
	setHideFutureQuestionTips: (hide: boolean) => void;
	setAutoRedeemLastPromptAt: (userLogicalId: string) => void;
	requestAutoRedeemPrompt: () => void;
	resetGlobalStore: () => void;
	setSelectedApp: (app: Apps) => void;
	setAdminPaywallEnabled: (adminPaywallEnabled: boolean) => void;
	setIsAuthFlowExamContextSwitching: (isSwitching: boolean) => void;
	setIsPremiumUser: (isPremiumUser: boolean) => void;
	setLastLoggedInEmail: (email: string) => void;
	setHasShownMasteryTopicsAlert: (hasShown: boolean) => void;
	setHasShownSummativeTestAlert: (hasShown: boolean) => void;
	setHasShownConfidenceOnboardingDialog: (hasShown: boolean) => void;
	setShowAvatarUpdatedMessage: (hasShown: boolean) => void;
	setLocale: (locale: string | undefined) => void;
	toggleAudioPlaybackTestDialog: () => void;
	setCountry: (country: Country) => void;
	settingsEnabledFeatures: Record<Features, boolean>;
	toggleFeature: (feature: Features) => void;
	setHasShownChallengeInstructions: (hasShown: boolean) => void;
	setPendingConsent: (consent: ConsentData | undefined) => void;
};

const initialState = {
	hasShownSummativeTestAlert: undefined,
	hasShownConfidenceOnboardingDialog: undefined,
	hasShownMasteryTopicsAlert: undefined,
	lastLoggedInEmail: undefined,
	showAvatarUpdatedMessage: undefined,
	locale: undefined,
	country: undefined,
	showAudioPlaybackTestDialog: true,
	isPremiumUser: false,
	hasShownChallengeInstructions: undefined,
	selectedApp: undefined,
	pendingConsent: undefined,
	adminPaywallEnabled: false,
	isAuthFlowExamContextSwitching: false,
	autoRedeemLastPromptAt: {},
	autoRedeemPromptRequestId: 0,
	shownQuestionTipTagIds: {},
	hideFutureQuestionTips: undefined,
	settingsEnabledFeatures: {
		hint: true,
		aila: true,
		haptic: true,
		notifications: true,
		alerts: true,
	},
};

/* -----------------Store--------------- */
export const useGlobalStore = create<State>()(
	persist(
		(set) => ({
			...initialState,
			setIsPremiumUser: (isPremiumUser) => set({ isPremiumUser }),
			setSelectedApp: (app) => set({ selectedApp: app }),
			setHasShownChallengeInstructions: (hasShown) =>
				set({ hasShownChallengeInstructions: hasShown }),
			setCountry: (country) => set({ country }),
			setHasShownMasteryTopicsAlert: (hasShown) =>
				set({ hasShownMasteryTopicsAlert: hasShown }),
			setHasShownSummativeTestAlert: (hasShown) =>
				set({ hasShownSummativeTestAlert: hasShown }),
			setHasShownConfidenceOnboardingDialog: (hasShown) =>
				set({ hasShownConfidenceOnboardingDialog: hasShown }),
			toggleFeature: (feature) =>
				set((state) => ({
					settingsEnabledFeatures: {
						...state.settingsEnabledFeatures,
						[feature]: !state.settingsEnabledFeatures[feature],
					},
				})),
			setLastLoggedInEmail: (email) => set({ lastLoggedInEmail: email }),
			setShowAvatarUpdatedMessage: (hasShown) =>
				set({ showAvatarUpdatedMessage: hasShown }),
			setLocale: (locale) => set({ locale }),
			toggleAudioPlaybackTestDialog: () =>
				set((state) => ({
					showAudioPlaybackTestDialog: !state.showAudioPlaybackTestDialog,
				})),
			setPendingConsent: (consent) => set({ pendingConsent: consent }),
			setAdminPaywallEnabled: (adminPaywallEnabled) =>
				set({ adminPaywallEnabled }),
			setIsAuthFlowExamContextSwitching: (isSwitching) =>
				set({ isAuthFlowExamContextSwitching: isSwitching }),
			setAutoRedeemLastPromptAt: (userLogicalId) =>
				set((state) => ({
					autoRedeemLastPromptAt: {
						...state.autoRedeemLastPromptAt,
						[userLogicalId]: Date.now(),
					},
				})),
			requestAutoRedeemPrompt: () =>
				set((state) => ({
					autoRedeemPromptRequestId: state.autoRedeemPromptRequestId + 1,
				})),
			setQuestionTipTagIdShown: (tagId) =>
				set((state) => ({
					shownQuestionTipTagIds: {
						...state.shownQuestionTipTagIds,
						[tagId]: true,
					},
				})),
			setHideFutureQuestionTips: (hide) =>
				set({ hideFutureQuestionTips: hide }),
			resetGlobalStore: () => set(initialState),
		}),

		{
			name: "global-store",
			storage: createJSONStorage(() => zustandStorage),
			partialize: (state) => {
				const {
					adminPaywallEnabled: _adminPaywallEnabled,
					isPremiumUser: _isPremiumUser,
					isAuthFlowExamContextSwitching: _isAuthFlowExamContextSwitching,
					autoRedeemPromptRequestId: _autoRedeemPromptRequestId,
					...rest
				} = state;
				return rest;
			},
		},
	),
);
